/**
 * DecisionTree view — read-only flowchart reference. Renders the same
 * SVG flowcharts and "Initial fee calculation" worked-example block
 * supplied by the source artifact. Kept verbatim — the wording is
 * deliberate and is shown alongside the live calculator.
 */

import React from "react";
import { C } from "./brand";
import { fmtZAR, fmtPct } from "./feeEngine";

export default function DecisionTree({ settings }) {
  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ color: C.tealDark, fontSize: 22, fontWeight: 600, marginBottom: 4 }}>
          Fee decision tree
        </h2>
        <p style={{ color: C.muted, fontSize: 13 }}>
          Quick reference. View only. Use the Calculator tab to run figures.
        </p>
      </div>

      {/* Initial fees */}
      <h3 style={{ color: C.tealDark, fontSize: 16, fontWeight: 600, marginBottom: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>
        Initial fee
      </h3>
      <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 4, padding: 24, marginBottom: 32, overflowX: "auto" }}>
        <InitialFeeFlow settings={settings} />
      </div>

      {/* Ongoing fees */}
      <h3 style={{ color: C.tealDark, fontSize: 16, fontWeight: 600, marginBottom: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>
        Ongoing fee
      </h3>
      <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 4, padding: 24, marginBottom: 12, overflowX: "auto" }}>
        <OngoingFeeFlow />
      </div>
      <div
        style={{
          background: "#F2F7F8",
          borderLeft: `3px solid ${C.tealMid}`,
          padding: "10px 14px",
          marginBottom: 32,
          fontSize: 12,
          color: C.text,
          lineHeight: 1.6,
        }}
      >
        <strong style={{ color: C.tealDark }}>Year-1 base for combined investments:</strong> when an investment includes
        both a lump sum and a recurring contribution, the ongoing fee is calculated on{" "}
        <strong>lump sum + (monthly × 12)</strong>. No growth assumed.
      </div>

      {/* Calculation reference */}
      <h3 style={{ color: C.tealDark, fontSize: 16, fontWeight: 600, marginBottom: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>
        Initial fee calculation
      </h3>
      <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 4, padding: 24 }}>
        <CalcReference settings={settings} />
      </div>
    </div>
  );
}

// --- SVG flowchart helpers ---
function FlowNode({ x, y, w = 220, h = 64, title, sub, kind = "default" }) {
  const palette = {
    decision: { fill: C.tealDark, stroke: C.tealDark, text: C.white, sub: "#C4D4D7" },
    branch: { fill: C.white, stroke: C.tealMid, text: C.tealDark, sub: C.muted },
    terminal: { fill: "#F2F7F8", stroke: C.tealMid, text: C.tealDark, sub: C.teal },
    result: { fill: "#FEF4EE", stroke: C.orange, text: C.tealDark, sub: C.muted },
    default: { fill: C.white, stroke: C.border, text: C.text, sub: C.muted },
  };
  const p = palette[kind] || palette.default;
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={6}
        ry={6}
        fill={p.fill}
        stroke={p.stroke}
        strokeWidth={1.5}
      />
      <text x={x + w / 2} y={sub ? y + h / 2 - 6 : y + h / 2 + 5} textAnchor="middle" fontSize="13" fontWeight="600" fill={p.text}>
        {title}
      </text>
      {sub && (
        <text x={x + w / 2} y={y + h / 2 + 12} textAnchor="middle" fontSize="11" fill={p.sub}>
          {sub}
        </text>
      )}
    </g>
  );
}

function FlowEdge({ from, to, label, labelOffset = 0 }) {
  const midX = (from.x + to.x) / 2;
  const midY = (from.y + to.y) / 2;
  // Right-angle path: vertical then horizontal then vertical
  const path =
    from.x === to.x
      ? `M ${from.x} ${from.y} L ${to.x} ${to.y}`
      : `M ${from.x} ${from.y} L ${from.x} ${midY} L ${to.x} ${midY} L ${to.x} ${to.y}`;
  return (
    <g>
      <path d={path} stroke={C.tealMid} strokeWidth={1.5} fill="none" markerEnd="url(#arrow)" />
      {label && (
        <text x={midX + labelOffset} y={midY - 4} textAnchor="middle" fontSize="11" fontWeight="600" fill={C.teal}>
          {label}
        </text>
      )}
    </g>
  );
}

function InitialFeeFlow({ settings }) {
  const W = 1000;
  const H = 700;
  const w = 200;
  const h = 64;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", maxWidth: 1000, height: "auto" }} role="img" aria-label="Initial fee decision flow">
      <defs>
        <marker id="arrow" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto">
          <path d="M0,0 L8,3 L0,6 z" fill={C.tealMid} />
        </marker>
      </defs>

      {/* Row 0: Initial decision */}
      <FlowNode x={400} y={20} w={w} h={h} title="Lump sum component?" kind="decision" />

      {/* Row 1: branches */}
      <FlowNode x={120} y={130} w={w} h={h} title="No (recurring only)" kind="branch" />
      <FlowNode x={680} y={130} w={w} h={h} title="Yes — has lump sum" kind="branch" />

      {/* Row 2 */}
      <FlowNode x={120} y={240} w={w} h={h} title="No initial fee" sub="See ongoing fee" kind="terminal" />
      <FlowNode x={680} y={240} w={w} h={h} title="New investment?" sub="vs transfer / existing" kind="decision" />

      {/* Row 3: outcomes */}
      <FlowNode x={420} y={350} w={w} h={h} title="No initial fee" sub="Transfer or existing" kind="terminal" />
      <FlowNode x={720} y={350} w={w} h={h} title={`Lump ≥ ${fmtZAR(settings.minThreshold)}?`} kind="decision" />

      {/* Row 4: threshold outcomes */}
      <FlowNode x={560} y={460} w={w} h={h} title="No initial fee" kind="terminal" />
      <FlowNode x={780} y={460} w={w} h={h} title="Calculate fee" sub="Hours × Rate ÷ Split" kind="result" />

      {/* Row 5 */}
      <FlowNode x={780} y={570} w={w} h={h} title="Cap at 1% of lump + VAT" kind="result" />

      {/* Edges */}
      <FlowEdge from={{ x: 500, y: 84 }} to={{ x: 220, y: 130 }} label="no" />
      <FlowEdge from={{ x: 500, y: 84 }} to={{ x: 780, y: 130 }} label="yes" />
      <FlowEdge from={{ x: 220, y: 194 }} to={{ x: 220, y: 240 }} />
      <FlowEdge from={{ x: 780, y: 194 }} to={{ x: 780, y: 240 }} />
      <FlowEdge from={{ x: 780, y: 304 }} to={{ x: 520, y: 350 }} label="no" />
      <FlowEdge from={{ x: 780, y: 304 }} to={{ x: 820, y: 350 }} label="yes" />
      <FlowEdge from={{ x: 820, y: 414 }} to={{ x: 660, y: 460 }} label="no" />
      <FlowEdge from={{ x: 820, y: 414 }} to={{ x: 880, y: 460 }} label="yes" />
      <FlowEdge from={{ x: 880, y: 524 }} to={{ x: 880, y: 570 }} />
    </svg>
  );
}

function OngoingFeeFlow() {
  const W = 1060;
  const H = 540;
  const w = 220;
  const h = 64;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", maxWidth: 1060, height: "auto" }} role="img" aria-label="Ongoing fee decision flow">
      <defs>
        <marker id="arrow2" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto">
          <path d="M0,0 L8,3 L0,6 z" fill={C.tealMid} />
        </marker>
      </defs>

      {/* Row 0: top decision */}
      <FlowNode x={370} y={20} w={w} h={h} title="Portfolio composition?" kind="decision" />

      {/* Row 1: three branches */}
      <FlowNode x={40} y={140} w={w} h={h} title="100% Optimum BCI" sub="Income Fund" kind="branch" />
      <FlowNode x={370} y={140} w={w} h={h} title="100% OIG Capital" sub="Edge Model" kind="branch" />
      <FlowNode x={700} y={140} w={w} h={h} title="Any Other Allocation" kind="branch" />

      {/* Row 2: results for the two special funds; split decision for the third */}
      <FlowNode x={40} y={280} w={w} h={h} title="0.10% p.a." sub="+ VAT" kind="result" />
      <FlowNode x={370} y={280} w={w} h={h} title="0.15% p.a." sub="+ VAT" kind="result" />
      <FlowNode x={700} y={280} w={w} h={h} title="Split percentage?" kind="decision" />

      {/* Row 3: split outcomes */}
      <FlowNode x={580} y={420} w={w} h={h} title="0.25% p.a." sub="+ VAT" kind="result" />
      <FlowNode x={820} y={420} w={w} h={h} title="0.50% p.a." sub="+ VAT" kind="result" />

      {/* Edges: top decision → 3 branches */}
      <path d="M 480,84 L 150,140" stroke={C.tealMid} strokeWidth={1.5} fill="none" markerEnd="url(#arrow2)" />
      <path d="M 480,84 L 480,140" stroke={C.tealMid} strokeWidth={1.5} fill="none" markerEnd="url(#arrow2)" />
      <path d="M 480,84 L 810,140" stroke={C.tealMid} strokeWidth={1.5} fill="none" markerEnd="url(#arrow2)" />

      {/* Edges: special funds → results */}
      <path d="M 150,204 L 150,280" stroke={C.tealMid} strokeWidth={1.5} fill="none" markerEnd="url(#arrow2)" />
      <path d="M 480,204 L 480,280" stroke={C.tealMid} strokeWidth={1.5} fill="none" markerEnd="url(#arrow2)" />

      {/* Edge: Any Other → split decision */}
      <path d="M 810,204 L 810,280" stroke={C.tealMid} strokeWidth={1.5} fill="none" markerEnd="url(#arrow2)" />

      {/* Edges: split decision → outcomes (with labels) */}
      <path d="M 810,344 L 690,420" stroke={C.tealMid} strokeWidth={1.5} fill="none" markerEnd="url(#arrow2)" />
      <path d="M 810,344 L 930,420" stroke={C.tealMid} strokeWidth={1.5} fill="none" markerEnd="url(#arrow2)" />
      <text x={715} y={388} fontSize="11" fontWeight="600" fill={C.teal}>70%</text>
      <text x={855} y={388} fontSize="11" fontWeight="600" fill={C.teal}>less than 70%</text>
    </svg>
  );
}

function CalcReference({ settings }) {
  return (
    <div style={{ fontSize: 14, color: C.text, lineHeight: 1.8 }}>
      <div style={{ background: C.bgSoft, padding: 16, borderRadius: 4, fontFamily: "monospace", fontSize: 13, marginBottom: 16, color: C.tealDark }}>
        <div><strong>Step 1.</strong> targetEarnings = hours × {fmtZAR(settings.hourlyRate)}</div>
        <div><strong>Step 2.</strong> grossFee = targetEarnings ÷ split%</div>
        <div><strong>Step 3.</strong> if grossFee &gt; (lumpSum × {fmtPct(settings.feeCap, 0)}) → grossFee = lumpSum × {fmtPct(settings.feeCap, 0)}</div>
        <div><strong>Step 4.</strong> totalCharged = grossFee × (1 + {fmtPct(settings.vatRate, 0)})</div>
        <div><strong>Step 5.</strong> yourTakeHome = grossFee × split%</div>
      </div>
      <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>
        <strong style={{ color: C.tealDark }}>Worked example.</strong>{" "}
        5 hours, 70% split, R500,000 lump sum →
        targetEarnings = {fmtZAR(5 * settings.hourlyRate)}; uncapped gross ={" "}
        {fmtZAR((5 * settings.hourlyRate) / 0.7)}; cap (1%) =
        R5,000; gross = R5,000; total charged = R5,750; your take-home = R3,500 (cap reduces earnings).
      </p>
    </div>
  );
}
